/**
 * Accept Invite — public page hit via /admin/accept-invite?token=…
 *
 * Two phases:
 *   1. Preview (GET /api/admin/team/invite-preview/:token) — shows who invited
 *      them, what bundle they're getting, when the invite expires.
 *   2. Accept → POST /api/admin/team/accept-invite — creates the admin_users row,
 *      then "Sign in with Google" (/admin/login). The Admin Portal is Google sign-in
 *      only (2026-09-29): there is no password to set.
 *
 * Public — no admin auth. The invite_token IS the auth credential. Tokens
 * are 256-bit, single-use, and expire in 7 days by default.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Shield, UserPlus } from 'lucide-react';

interface InvitePreview {
  email: string;
  full_name: string | null;
  bundle_name: string;
  role: string;
  invited_at: string;
  expires_at: string;
  invited_by_name: string | null;
  notes: string | null;
}

export const AcceptInvitePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);

  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch preview on mount
  React.useEffect(() => {
    if (!token) {
      setPreviewError('Missing invitation token. Please use the link from your invitation email.');
      setPreviewLoaded(true);
      return;
    }
    fetch(`/api/admin/team/invite-preview/${encodeURIComponent(token)}`)
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Invitation not found or expired.');
        return r.json();
      })
      .then(r => {
        setPreview(r.data);
        setFullName(r.data.full_name || '');
        setPreviewLoaded(true);
      })
      .catch(e => { setPreviewError(e.message); setPreviewLoaded(true); });
  }, [token]);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      const resp = await fetch('/api/admin/team/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, full_name: fullName || null }),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed to create account.');
      }
      setSuccess(true);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        {!previewLoaded ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading invitation…</div>
        ) : previewError ? (
          <div className="text-center">
            <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
            <h1 className="text-lg font-bold text-gray-900">Invitation problem</h1>
            <p className="mt-2 text-sm text-gray-600">{previewError}</p>
            <p className="mt-4 text-xs text-gray-400">
              Ask the person who invited you to send a new link, or contact{' '}
              <a href="mailto:support@coachcedar.com" className="underline">support</a>.
            </p>
          </div>
        ) : success ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12" style={{ color: 'var(--cedar-primary)' }} />
            <h1 className="text-lg font-bold text-gray-900">Account created</h1>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to the Coach Cedar admin portal with Google, as {preview?.email}.
            </p>
            <button
              onClick={() => navigate('/admin/login')}
              className="mt-6 w-full rounded-md py-2.5 text-sm font-medium text-white"
              style={{ backgroundColor: 'var(--cedar-primary)' }}
            >
              Sign in with Google
            </button>
          </div>
        ) : preview && (
          <>
            <div className="mb-6 text-center">
              <div
                className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
                style={{ backgroundColor: 'rgba(31, 108, 79, 0.10)' }}
              >
                <UserPlus className="h-6 w-6" style={{ color: 'var(--cedar-primary)' }} />
              </div>
              <h1 className="text-lg font-bold text-gray-900">
                Welcome to Coach Cedar
              </h1>
              <p className="mt-1 text-xs text-gray-500">
                {preview.invited_by_name ? `${preview.invited_by_name} invited you` : 'You\'ve been invited'} to join
                as <strong style={{ color: 'var(--cedar-primary)' }}>{preview.bundle_name}</strong>.
              </p>
            </div>

            <div className="mb-6 rounded-md bg-gray-50 p-3 text-xs">
              <Row label="Email" value={preview.email} mono />
              <Row label="Role" value={preview.role.replace('_', ' ')} />
              <Row label="Bundle" value={preview.bundle_name} />
              <Row label="Expires" value={new Date(preview.expires_at).toLocaleDateString()} />
              {preview.notes && (
                <div className="mt-2 border-t border-gray-200 pt-2 text-[11px] italic text-gray-700">
                  "{preview.notes}"
                </div>
              )}
            </div>

            <form onSubmit={handleAccept} className="space-y-4">
              <Field label="Your name">
                <input
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="How you'd like to be displayed"
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                />
              </Field>

              {submitError && (
                <div className="rounded-md bg-red-50 p-3 text-xs text-red-700">{submitError}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--cedar-primary)' }}
              >
                <Shield className="h-4 w-4" />
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            </form>

            <p className="mt-4 text-center text-[10px] text-gray-400">
              By accepting, you confirm you're authorized to access Coach Cedar admin tools.
              Single-use link expires {new Date(preview.expires_at).toLocaleString()}.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-baseline gap-3 py-0.5">
    <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
    <span className={mono ? 'font-mono text-gray-800' : 'text-gray-800'}>{value}</span>
  </div>
);

const Field: React.FC<{ label: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ label, icon, children }) => (
  <label className="block">
    <span className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
      {icon}
      {label}
    </span>
    {children}
  </label>
);

export default AcceptInvitePage;
